// src/components/network/feed/PostItem.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  Heart, MessageSquare, Share2,
  ExternalLink, Building, Clock, Send, User, Check, FileText, Download,
  Newspaper, CalendarDays, MapPin, Ticket
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../firebase/config';
import { 
  doc, updateDoc, increment, setDoc, deleteDoc,
  collection, addDoc, query, where, orderBy, getDocs,
  getDoc, serverTimestamp 
} from 'firebase/firestore';
import PostActionsMenu from './PostActionsMenu';
import PostEditModal from './PostEditModal';
import PostReportModal from './PostReportModal';
import DeletePostModal from './DeletePostModal';

const PostItem = ({ post, onUpdatePost, onDeletePost, onUnsave }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [liked, setLiked] = useState(post.userHasLiked || false);
  const [likeCount, setLikeCount] = useState(post.likeCount || 0);
  const [saved, setSaved] = useState(post.userHasSaved || false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const [reporting, setReporting] = useState(false);
  const [reportError, setReportError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const isOwner = !!currentUser && currentUser.uid === post.authorId;

  const copyShareUrl = (shareUrl) => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  };

  const incrementShareCount = async () => {
    try {
      await updateDoc(doc(db, 'posts', post.id), {
        shareCount: increment(1)
      });

      if (onUpdatePost) {
        onUpdatePost({
          ...post,
          shareCount: (post.shareCount || 0) + 1
        });
      }
    } catch (error) {
      console.error('Error incrementing share count:', error);
    }
  };

  const handleToggleLike = async () => {
    if (!currentUser) {
      navigate('/login', { state: { redirectTo: `/network` } });
      return;
    }
    
    const isCurrentlyLiked = liked;

    try {
      // Store the current liked state before changing it
      const isCurrentlyLiked = liked;
      const likeRef = doc(db, 'postLikes', `${currentUser.uid}_${post.id}`);
      const postRef = doc(db, 'posts', post.id);
      
      // Optimistically update UI
      setLiked(!isCurrentlyLiked);
      setLikeCount(prev => isCurrentlyLiked ? prev - 1 : prev + 1);
      
      if (isCurrentlyLiked) {
        // Unlike
        await deleteDoc(likeRef);
        await updateDoc(postRef, {
          likeCount: increment(-1)
        });
      } else {
        // Like
        await setDoc(likeRef, {
          userId: currentUser.uid,
          postId: post.id,
          createdAt: serverTimestamp()
        });
        await updateDoc(postRef, {
          likeCount: increment(1)
        });
      }
      
      // Update the post in parent component if callback provided
      if (onUpdatePost) {
        onUpdatePost({
          ...post,
          userHasLiked: !isCurrentlyLiked,
          likeCount: isCurrentlyLiked ? likeCount - 1 : likeCount + 1
        });
      }
    } catch (error) {
      // Revert UI changes on error
      console.error('Error toggling like:', error);
      setLiked(isCurrentlyLiked);
      setLikeCount(prev => isCurrentlyLiked ? prev + 1 : prev - 1);
    }
  };

  // Handle save/unsave
  const handleToggleSave = async () => {
    if (!currentUser) {
      navigate('/login', { state: { redirectTo: `/network` } });
      return;
    }
    
    const isCurrentlySaved = saved;

    try {
      // Store the current saved state before changing it
      const savedRef = doc(db, 'savedPosts', `${currentUser.uid}_${post.id}`);
      
      // Optimistically update UI
      setSaved(!isCurrentlySaved);
      
      if (isCurrentlySaved) {
        // Unsave
        await deleteDoc(savedRef);
        if (onUnsave && post.savedId) {
          onUnsave(post.id, post.savedId);
          return;
        }
      } else {
        // Save
        await setDoc(savedRef, {
          userId: currentUser.uid,
          postId: post.id,
          createdAt: serverTimestamp()
        });
      }
      
      // Update the post in parent component if callback provided
      if (onUpdatePost) {
        onUpdatePost({
          ...post,
          userHasSaved: !isCurrentlySaved
        });
      }
    } catch (error) {
      // Revert UI changes on error
      console.error('Error toggling save:', error);
      setSaved(isCurrentlySaved);
    }
  };

  const handleStartEdit = () => {
    if (!isOwner) {
      return;
    }

    setEditError('');
    setShowEditModal(true);
  };

  const handleSaveEdit = async (formData) => {
    if (!isOwner) {
      return;
    }

    const trimmedContent = formData.content.trim();
    const trimmedTitle = formData.title.trim();

    if (!trimmedContent) {
      setEditError('Please enter some content for your post.');
      return;
    }

    if ((post.postType === 'article' || post.postType === 'event') && !trimmedTitle) {
      setEditError('Please enter a title before saving.');
      return;
    }

    if (post.postType === 'event' && !formData.eventDate) {
      setEditError('Please select an event date before saving.');
      return;
    }

    try {
      setSavingEdit(true);
      setEditError('');

      const updateData = {
        content: trimmedContent,
        updatedAt: serverTimestamp()
      };

      if (post.postType === 'article' || post.postType === 'event') {
        updateData.title = trimmedTitle;
      }

      if (post.postType === 'article') {
        updateData.subtitle = formData.subtitle.trim() || null;
      }

      if (post.postType === 'event') {
        updateData.eventVenue = formData.eventVenue.trim() || null;
        updateData.eventDate = formData.eventDate || null;
        updateData.eventBookingLink = formData.eventBookingLink.trim() || null;
      }

      await updateDoc(doc(db, 'posts', post.id), updateData);

      const updatedPost = {
        ...post,
        ...updateData,
        updatedAt: new Date()
      };

      if (onUpdatePost) {
        onUpdatePost(updatedPost);
      }

      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating post:', error);
      setEditError('Failed to save changes. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleOpenReport = () => {
    if (!currentUser) {
      navigate('/login', { state: { redirectTo: `/network/post/${post.id}` } });
      return;
    }

    setReportError('');
    setShowReportModal(true);
  };

  const handleSubmitReport = async ({ reason, details }) => {
    if (!currentUser) {
      navigate('/login', { state: { redirectTo: `/network/post/${post.id}` } });
      return;
    }

    try {
      setReporting(true);
      setReportError('');

      await setDoc(doc(db, 'postReports', `${currentUser.uid}_${post.id}`), {
        postId: post.id,
        reporterId: currentUser.uid,
        authorId: post.authorId || null,
        reason,
        details: details || null,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setShowReportModal(false);
    } catch (error) {
      console.error('Error reporting post:', error);
      setReportError('Failed to submit the report. Please try again.');
    } finally {
      setReporting(false);
    }
  };

  const handleDeletePost = async () => {
    if (!isOwner) {
      return;
    }

    try {
      setDeleting(true);

      const deleteMatches = async (collectionName) => {
        const snapshot = await getDocs(query(collection(db, collectionName), where('postId', '==', post.id)));
        await Promise.all(snapshot.docs.map((item) => deleteDoc(item.ref)));
      };

      await Promise.all([
        deleteMatches('comments'),
        deleteMatches('postLikes'),
        deleteMatches('savedPosts'),
        deleteMatches('postReports')
      ]);

      await deleteDoc(doc(db, 'posts', post.id));

      if (onDeletePost) {
        onDeletePost(post.id);
      }
    } catch (error) {
      console.error('Error deleting post:', error);
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };
  
  // Fetch comments when showing comments section
  useEffect(() => {
    const fetchComments = async () => {
      if (!showComments) return;
      
      try {
        setLoadingComments(true);
        
        const commentsQuery = query(
          collection(db, 'comments'),
          where('postId', '==', post.id),
          orderBy('createdAt', 'asc')
        );
        
        const commentsSnapshot = await getDocs(commentsQuery);
        
        // Process comments and get author info
        const commentsPromises = commentsSnapshot.docs.map(async (commentDoc) => {
          const commentData = commentDoc.data();
          
          // Get author data
          let authorData = { id: commentData.authorId, name: 'Unknown User' };
          
          try {
            // First check if it's a business
            const businessRef = doc(db, 'businesses', commentData.authorId);
            const businessSnap = await getDoc(businessRef);
            
            if (businessSnap.exists()) {
              authorData = {
                id: businessSnap.id,
                ...businessSnap.data(),
                type: 'business'
              };
            } else {
              // If not a business, check users collection
              const userRef = doc(db, 'users', commentData.authorId);
              const userSnap = await getDoc(userRef);
              
              if (userSnap.exists()) {
                const userData = userSnap.data();
                authorData = {
                  id: userSnap.id,
                  name: userData.displayName || userData.email,
                  email: userData.email,
                  type: 'user'
                };
              }
            }
          } catch (err) {
            console.error('Error fetching comment author:', err);
          }
          
          return {
            id: commentDoc.id,
            ...commentData,
            createdAt: commentData.createdAt?.toDate() || new Date(),
            author: authorData
          };
        });
        
        const processedComments = await Promise.all(commentsPromises);
        setComments(processedComments);
      } catch (error) {
        console.error('Error fetching comments:', error);
      } finally {
        setLoadingComments(false);
      }
    };
    
    fetchComments();
  }, [showComments, post.id]);
  
  // Add a comment
  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      navigate('/login', { state: { redirectTo: `/network` } });
      return;
    }
    
    if (!commentText.trim()) return;
    
    try {
      setSubmittingComment(true);
      
      // Add comment to Firestore
      const commentData = {
        postId: post.id,
        authorId: currentUser.uid,
        content: commentText.trim(),
        createdAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'comments'), commentData);
      
      // Update comment count on the post
      const postRef = doc(db, 'posts', post.id);
      await updateDoc(postRef, {
        commentCount: increment(1)
      });
      
      // Add to local state without waiting for a refetch
      let currentUserInfo = { 
        id: currentUser.uid,
        name: currentUser.displayName || currentUser.email,
        email: currentUser.email,
        type: 'user'
      };
      
      // Try to get user profile for better display info
      try {
        const userProfileRef = doc(db, 'userProfiles', currentUser.uid);
        const userProfileSnap = await getDoc(userProfileRef);
        
        if (userProfileSnap.exists()) {
          const profileData = userProfileSnap.data();
          currentUserInfo = {
            ...currentUserInfo,
            name: `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim() || currentUser.email,
            profileImageUrl: profileData.profileImageUrl
          };
        }
      } catch (err) {
        console.error('Error getting user profile:', err);
      }
      
      const newComment = {
        id: docRef.id,
        ...commentData,
        createdAt: new Date(),
        author: currentUserInfo
      };
      
      setComments(prev => [...prev, newComment]);
      setCommentText('');
      
      // Update the comment count in the post object
      if (onUpdatePost) {
        onUpdatePost({
          ...post,
          commentCount: (post.commentCount || 0) + 1
        });
      }
      
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };
  
  // Toggle comments visibility
  const toggleComments = () => {
    setShowComments(!showComments);
  };

  // Handle Share button
  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/share/post/${post.id}`;
    const shareData = {
      title: post.title || (post.author?.name ? `Post by ${post.author.name}` : 'Post on BizCentral'),
      text: post.subtitle || post.content?.slice(0, 100) || 'Check out this post on BizCentral',
      url: shareUrl
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        await incrementShareCount();
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyShareUrl(shareUrl);
          await incrementShareCount();
        }
      }
    } else {
      copyShareUrl(shareUrl);
      await incrementShareCount();
    }
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch (e) {
      return '';
    }
  };

  const authorProfileId = post.author?.id || post.businessId || post.authorId || '';
  const authorDisplayName = post.author?.name || post.authorName || 'Unknown Business';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-2">
      {/* Post Header */}
      <div className="flex items-center p-4 border-b border-gray-100">
      <div className="w-12 h-12 rounded-full bg-gray-100 mr-3 flex-shrink-0 overflow-hidden">
          {post.author?.businessLogoUrl ? (
            <img 
              src={post.author.businessLogoUrl} 
              alt={post.author.name} 
              className="w-full h-full object-cover"
            />
          ) : post.authorLogoUrl ? (
            <img 
              src={post.authorLogoUrl} 
              alt={post.authorName || 'Author'} 
              className="w-full h-full object-cover"
            />
          ) : post.author?.profileImageUrl ? (
            <img 
              src={post.author.profileImageUrl} 
              alt={post.author.name} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
              <Building size={20} />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
        <Link
            to={`/network/profile/${authorProfileId}`}
            className="font-medium text-gray-900 hover:text-[#190192]"
          >
            {authorDisplayName}
          </Link>
          <div className="flex items-center text-gray-500 text-sm">
            <Clock size={14} className="mr-1" />
            <span>{formatDate(post.createdAt)}</span>
          </div>
        </div>
        <PostActionsMenu
          isOwner={isOwner}
          isSaved={saved}
          onToggleSave={handleToggleSave}
          onEdit={handleStartEdit}
          onReport={handleOpenReport}
          onDelete={() => setShowDeleteModal(true)}
        />
      </div>

      {/* Post Content */}
      <div className="p-0">
        {/* Article header */}
        {post.postType === 'article' && post.title && (
          <div className="px-4 pt-4">
            <div className="flex items-center gap-1.5 text-xs text-[#190192] font-medium mb-2">
              <Newspaper size={12} />
              <span>ARTICLE</span>
            </div>
            <Link to={`/network/post/${post.id}`} className="block group">
              <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#190192] leading-tight">
                {post.title}
              </h3>
              {post.subtitle && (
                <p className="text-sm text-gray-500 mt-1">{post.subtitle}</p>
              )}
            </Link>
          </div>
        )}

        {/* Event header */}
        {post.postType === 'event' && post.title && (
          <div className="px-4 pt-4">
            <div className="flex items-center gap-1.5 text-xs text-[#190192] font-medium mb-2">
              <CalendarDays size={12} />
              <span>EVENT</span>
            </div>
            <Link to={`/network/post/${post.id}`} className="block group">
              <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#190192] leading-tight">
                {post.title}
              </h3>
            </Link>
            <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-600">
              {post.eventDate && (
                <div className="flex items-center gap-1.5">
                  <CalendarDays size={14} className="text-[#190192]" />
                  <span>{new Date(post.eventDate).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
              {post.eventVenue && (
                <div className="flex items-center gap-1.5">
                  <MapPin size={14} className="text-[#190192]" />
                  <span>{post.eventVenue}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Post body text */}
        {post.postType === 'article' ? (
          <div className="px-4 pt-2 pb-1">
            <div className="prose prose-sm max-w-none text-gray-800 line-clamp-6 prose-headings:text-gray-900 prose-a:text-[#190192] prose-a:no-underline hover:prose-a:underline prose-pre:bg-gray-50 prose-code:text-[#190192] prose-code:bg-purple-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:mb-1.5 prose-headings:mt-3 prose-blockquote:my-1.5 prose-pre:my-1.5 prose-hr:my-2">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
            </div>
            {post.content?.length > 200 && (
              <Link
                to={`/network/post/${post.id}`}
                className="text-[#190192] hover:underline text-sm font-medium mt-1 inline-block"
              >
                Read full article
              </Link>
            )}
          </div>
        ) : (
          <div className="p-4">
            {post.content?.length > 300 ? (
              <>
                <div className="prose prose-sm max-w-none text-gray-800 prose-headings:text-gray-900 prose-a:text-[#190192] prose-a:no-underline hover:prose-a:underline prose-pre:bg-gray-50 prose-code:text-[#190192] prose-code:bg-purple-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:mb-1.5 prose-headings:mt-3 prose-blockquote:my-1.5 prose-pre:my-1.5 prose-hr:my-2">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content.slice(0, 300) + '...'}</ReactMarkdown>
                </div>
                <Link
                  to={`/network/post/${post.id}`}
                  className="text-[#190192] hover:underline text-sm font-medium"
                >
                  See more
                </Link>
              </>
            ) : (
              <div className="prose prose-sm max-w-none text-gray-800 prose-headings:text-gray-900 prose-a:text-[#190192] prose-a:no-underline hover:prose-a:underline prose-pre:bg-gray-50 prose-code:text-[#190192] prose-code:bg-purple-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:mb-1.5 prose-headings:mt-3 prose-blockquote:my-1.5 prose-pre:my-1.5 prose-hr:my-2">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
              </div>
            )}
          </div>
        )}

        {/* Event booking link */}
        {post.postType === 'event' && post.eventBookingLink && (
          <div className="px-4 pb-2">
            <a
              href={post.eventBookingLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#190192] text-white rounded-lg text-sm font-medium hover:bg-[#2C0DB5] transition-colors"
            >
              <Ticket size={16} />
              Book / RSVP
            </a>
          </div>
        )}
        
        {post.imageUrl && (
          <div className="block mt-3 p-0">
            <img
              src={post.imageUrl}
              alt="Post attachment"
              className="rounded-sm w-full object-cover"
            />
          </div>
        )}

        {/* YouTube Video Embed */}
        {post.videoUrl && (
          <div className="mt-3 aspect-video">
            <iframe
              src={`${post.videoUrl}?autoplay=1&mute=1&loop=1`}
              title="Video"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}

        {/* Document Attachment */}
        {post.documentUrl && (
          <div className="mt-3 mx-4">
            <a
              href={post.documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
            >
              <FileText size={24} className="text-[#190192] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {post.documentName || 'Document'}
                </p>
                <p className="text-xs text-gray-500">Tap to view document</p>
              </div>
              <Download size={18} className="text-gray-400 flex-shrink-0" />
            </a>
          </div>
        )}

        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="mt-0 flex flex-wrap gap-2">
            {post.tags.map(tag => (
              <Link 
                key={tag} 
                to={`/network/tag/${tag}`} 
                className="px-2 py-1 bg-purple-50 text-[#190192] text-xs rounded-full hover:bg-purple-100"
              >
                #{tag}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Post Actions */}
      <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center space-x-6">
          <button
            className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-md ${
              liked ? 'text-red-500' : 'text-gray-500 hover:bg-gray-100'
            }`}
            onClick={handleToggleLike}
          >
            {likeCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-gray-700 text-white text-[10px] font-medium rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                {likeCount > 99 ? '99+' : likeCount}
              </span>
            )}
            <Heart size={18} className={liked ? 'fill-current' : ''} />
            <span>{liked ? 'Liked' : 'Like'}</span>
          </button>

          <button
            className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-gray-500 hover:bg-gray-100"
            onClick={toggleComments}
          >
            {(post.commentCount || 0) > 0 && (
              <span className="absolute -top-2 -right-2 bg-gray-700 text-white text-[10px] font-medium rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                {post.commentCount > 99 ? '99+' : post.commentCount}
              </span>
            )}
            <MessageSquare size={18} />
            <span>Comment</span>
          </button>

          <button
            className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-md ${
              shareCopied ? 'text-green-600 bg-green-50' : 'text-gray-500 hover:bg-gray-100'
            }`}
            onClick={handleShare}
          >
            {(post.shareCount || 0) > 0 && (
              <span className="absolute -top-2 -right-2 bg-gray-700 text-white text-[10px] font-medium rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                {post.shareCount > 99 ? '99+' : post.shareCount}
              </span>
            )}
            {shareCopied ? <Check size={18} /> : <Share2 size={18} />}
            <span>{shareCopied ? 'Copied!' : 'Share'}</span>
          </button>
        </div>

        <div className="text-xs text-gray-500">
          {(post.viewCount || 0) > 0 ? `${post.viewCount > 99 ? '99+' : post.viewCount} views` : ' '}
        </div>
      </div>
      
      {/* Comments Section */}
      {showComments && (
        <div className="px-4 py-3 border-t border-gray-100">
          {/* Comments list */}
          <div className="space-y-4 mb-4">
            {loadingComments ? (
              <div className="animate-pulse space-y-4">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="flex">
                    <div className="h-8 w-8 bg-gray-200 rounded-full mr-3"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : comments.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No comments yet. Be the first to share your thoughts!
              </p>
            ) : (
              comments.map(comment => (
                <div key={comment.id} className="flex">
                  <div className="w-8 h-8 rounded-full bg-gray-100 mr-3 flex-shrink-0 overflow-hidden">
                    {comment.author?.profileImageUrl ? (
                      <img 
                        src={comment.author.profileImageUrl} 
                        alt={comment.author.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : comment.author?.type === 'business' && comment.author?.businessLogoUrl ? (
                      <img 
                        src={comment.author.businessLogoUrl} 
                        alt={comment.author.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : comment.author?.type === 'business' ? (
                      <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
                        <Building size={16} />
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-600">
                        <User size={16} />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900">{comment.author?.name}</span>
                        <span className="text-xs text-gray-500">{formatDate(comment.createdAt)}</span>
                      </div>
                      <p className="text-gray-700">{comment.content}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Comment form */}
          {currentUser ? (
            <form onSubmit={handleAddComment} className="flex">
              <input
                type="text"
                className="flex-1 border border-gray-200 rounded-l-lg px-3 py-2 focus:outline-none focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                placeholder="Add a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                disabled={submittingComment}
              />
              <button
                type="submit"
                disabled={submittingComment || !commentText.trim()}
                className={`px-4 py-2 bg-[#190192] text-white rounded-r-lg ${
                  submittingComment || !commentText.trim() 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:bg-[#2C0DB5]'
                }`}
              >
                {submittingComment ? (
                  <div className="animate-spin h-5 w-5 border-2 border-white rounded-full border-t-transparent"></div>
                ) : (
                  <Send size={18} />
                )}
              </button>
            </form>
          ) : (
            <div className="text-center py-2 border border-gray-200 rounded-lg bg-gray-50">
              <p className="text-gray-600 text-sm">
                <Link to="/login" className="text-[#190192] hover:underline">Sign in</Link> to leave a comment
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* View Full Post Link */}
      <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
        <Link
          to={`/network/post/${post.id}`}
          className="text-[#190192] hover:text-[#2C0DB5] text-sm font-medium flex items-center justify-center"
        >
          View Full Post <ExternalLink size={14} className="ml-1" />
        </Link>
      </div>

      <PostEditModal
        post={post}
        isOpen={showEditModal}
        saving={savingEdit}
        error={editError}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveEdit}
      />

      <PostReportModal
        isOpen={showReportModal}
        submitting={reporting}
        error={reportError}
        onClose={() => setShowReportModal(false)}
        onSubmit={handleSubmitReport}
      />

      <DeletePostModal
        isOpen={showDeleteModal}
        deleting={deleting}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeletePost}
      />
    </div>
  );
};

export default PostItem;
